import { useContext, useMemo, useEffect, useState } from 'react';
import { WorkflowConfiguration } from './components';
import { WorkflowContext } from 'providers/workflow-provider';
import { ButtonRow, CardComponent, CardRow, Popup, Textarea, Textbox, TitleSection, Docker, BottomSection, NoRecord } from 'components/generic';
import { AnimatePresence, motion } from 'framer-motion';
import { cloneDeep } from 'lodash';
import { workflowInitialNode, workflowDockerlist } from 'config';
import { useFetch } from 'hooks';
import Images from 'assets/Images';
import './style.scss';

const Workflow = () => {
	const context = useContext(WorkflowContext);
	const [activeWf, setActiveWf] = useState(0);
	const {
		dedupError,
		setDedupError,
		savePopupState,
		setSavePopupState,
		addWorkflowToggler,
		selectedCard,
		setSelectedCard,
		setAddWorkflowToggler,
		createObj,
		setNodeFlow,
		setInitObj,
		setCreateObj,
		reset,
		dockerState,
		setDockerState,
		listingData,
		setComponentState
	} = context;

	const api = useFetch();

	const workflowListingAPI = async () => {
		const { data } = await api({ url: `workflow/v1/list`, method: 'post' });
		context.setListingData(data?.data?.at()?.results.map(el => ({ el, title: el?.wf_name, description: el?.wf_description, updatedAt: el?.updatedAt, updatedBy: el?.updatedBy, value: el?.bfm_loadCode })));
		reset({ createObj: true });
	};

	const templateListAPI = async () => {
		const apiReq = { latest: 'Y', templateTypes: ['sms', 'whatsapp', 'email'] };
		const { data } = await api({ url: 'list', template: true, method: 'post', data: apiReq });
		context.setTemplateList(data?.data?.at()?.result);
	};

	const conditionListAPI = async () => {
		const { data } = await api({ url: `workflow/v1/condition/list`, method: 'post' });
		context.setEntryConditions(data?.data?.at()?.results?.filter(el => el.wfc_type === 'ENTRY'));
		context.setExitConditions(data?.data?.at()?.results?.filter(el => el.wfc_type === 'EXIT'));
	};

	useEffect(() => {
		workflowListingAPI();
		conditionListAPI();
		templateListAPI();
		reset({ createObj: true });
	}, []);

	const addButtonToggler = () => {
		setAddWorkflowToggler(true);
	};

	const handleCreateObj = (data, key) => {
		setCreateObj(prevState => {
			const obj = cloneDeep(prevState);
			obj[key] = data;
			return obj;
		});
	};

	const handleButtonClick = e => {
		e === 'Goback' && reset({ createObj: true });
		if (e === 'Create') {
			setDockerState('add');
			const obj = cloneDeep(workflowInitialNode);
			obj.data.title = createObj?.wf_name;
			obj.data.description = createObj?.wf_description;
			setNodeFlow(obj, 0);
		}
	};

	const handleCheckbox = (e, item) => {
		setSelectedCard(e ? item?.el?.wf_id : false);
		setActiveWf(e ? item?.el?.active_wsl_count : null);
		!e && reset({ createObj: true });
	};

	const workflowDedupCheck = async e => {
		try {
			e && (await api({ url: `workflow/v1/workflowDedupeCheck/${e}`, method: 'get', hideErrorToaster: true }));
			e && setDedupError(false);
		} catch (error) {
			setDedupError(true);
		}
	};

	const handleCardToggler = async (wf_status, wf_id) => {
		await api({ url: `workflow/v1/updateStatus`, method: 'post', data: { wf_id, wf_status: wf_status ? 'A' : 'I' }, successToaster: true });
		workflowListingAPI();
	};

	// listing cards
	const cardsDisplay = useMemo(() => {
		return listingData?.map((el, i) => (
			<CardComponent
				toggle
				toggleOn={el?.el?.wf_status === 'A'}
				handleToggle={e => handleCardToggler(e, el?.el?.wf_id)}
				halfWidth
				key={i}
				item={el}
				checkbox
				checked={selectedCard === el?.el?.wf_id}
				handleChecked={e => handleCheckbox(e, el)}
			/>
		));
	}, [listingData, selectedCard]);

	const getWorkflowById = async e => {
		const { data } = await api({ url: `workflow/v1/${e?.list?.at()}`, method: 'get' });
		const createObj = data?.data?.at();
		setDockerState(e?.key);
		setCreateObj(createObj);
		setInitObj(createObj);
		context.setNodes(createObj?.wf_nodes);
		context.setEdges(createObj?.wf_edges);
	};

	const deleteWorkflowById = async e => {
		await api({ url: `workflow/v1/${e}`, method: 'delete', successToaster: true });
		workflowListingAPI();
	};

	const triggerWorkflow = async e => {
		await api({ url: `workflow/v1/trigger/${e}/APPLICATION`, method: 'get', successToaster: true });
		reset({ createObj: true });
	};

	const handleDocker = async e => {
		e?.key === 'cancel' && reset({ createObj: true });
		e?.key === 'trigger' && (await triggerWorkflow(e?.list?.at()));
		['edit', 'view'].includes(e?.key) && getWorkflowById(e);
		['delete'].includes(e?.key) && setDockerState(e?.key);
	};

	const addWorkflowAPI = async () => {
		await api({ url: `workflow/v1/${dockerState}`, method: 'post', data: createObj, successToaster: true });
		workflowListingAPI();
	};

	const handleSaveDeletePopup = async (e, type) => {
		type === 'save' && (e === 'Yes' ? addWorkflowAPI() : setSavePopupState(null));
		type === 'delete' && (e === 'Yes' ? deleteWorkflowById(selectedCard) : reset({ createObj: true }));
		type === 'goback' && (e === 'Yes' ? reset({ createObj: true }) : setSavePopupState(null));
	};

	// popup confirmation content for save and delete
	const saveDeletePopupContent = ({ label, type, subContent }) => {
		return (
			<div className='bim__confirmation'>
				<img src={Images.warningPopup} alt='warning' />
				<h3>Warning</h3>
				{subContent && <h4 className='subContent'>{subContent}</h4>}
				<p>{label}</p>
				<ButtonRow label='No' label1='Yes' handleButtonClick={e => handleSaveDeletePopup(e, type)} />
			</div>
		);
	};

	const closePopup = e => {
		e === 'delete' && reset({ createObj: true });
		e === 'save' && setSavePopupState(null);
	};

	// popup content for delete
	const deletePopup = useMemo(() => {
		return (
			<Popup onClose={() => closePopup('delete')} popupState={dockerState === 'delete'}>
				{saveDeletePopupContent({ label: `Are you sure you want to delete?`, subContent: activeWf ? `${activeWf} accounts are currently active.` : '', type: 'delete' })}
			</Popup>
		);
	}, [dockerState, activeWf]);

	// popup content for going back and save
	const gobackSavePopup = useMemo(() => {
		return (
			<Popup onClose={() => closePopup('save')} popupState={savePopupState?.length > 0}>
				{saveDeletePopupContent({ label: savePopupState === 'Save' ? 'Are you sure you want to save?' : 'Do you want to go back to dashboard?', type: savePopupState?.toLowerCase() })}
			</Popup>
		);
	}, [savePopupState]);

	const addPopupContent = useMemo(
		() => (
			<Popup onClose={() => handleButtonClick('Goback')} popupState={addWorkflowToggler}>
				<div className='bim__addpopup'>
					<h3>Create Workflow</h3>
					<Textbox
						label='Workflow Name'
						infoContent='Enter Workflow Name'
						mandatory
						onBlur={e => workflowDedupCheck(e?.target?.value)}
						error={dedupError}
						errorMessage={dedupError ? 'Workflow name already exists' : ''}
						placeholder='Enter Workflow Name'
						value={createObj?.wf_name}
						onChange={e => handleCreateObj(e?.target?.value, 'wf_name')}
						disabled={dockerState === 'view'}
					/>
					<Textarea
						label='Workflow Description'
						infoContent='Enter Workflow Description'
						mandatory
						maxlength={120}
						placeholder='Eg: DSS Level 1 configuration'
						value={createObj?.wf_description}
						onChange={e => handleCreateObj(e?.target?.value, 'wf_description')}
						disabled={dockerState === 'view'}
					/>
					<ButtonRow label='Goback' label1='Create' right disableSave={!createObj?.wf_name || !createObj?.wf_description || dedupError} handleButtonClick={handleButtonClick} />
				</div>
			</Popup>
		),
		[addWorkflowToggler, createObj]
	);

	const dockerContainer = useMemo(
		() => (
			<AnimatePresence>
				{selectedCard && (
					<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }}>
						<Docker dockerClick={handleDocker} dockerlist={workflowDockerlist} selectlist={[selectedCard]} />
					</motion.div>
				)}
			</AnimatePresence>
		),
		[selectedCard]
	);

	const listingDisplay = useMemo(
		() =>
			listingData?.length > 0 ? (
				<motion.div className='workflow scroll-bar' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }}>
					<CardRow>{cardsDisplay}</CardRow>
				</motion.div>
			) : (
				<NoRecord header='No workflows found!' description='Please add new' height='80%' />
			),
		[listingData, selectedCard]
	);

	const handleSaveRule = async e => {
		const url = `workflow/v1/condition/${context?.conditionType?.method}`;
		const data = { ...context.wfConditions, wfc_rules: e?.rule, wfc_type: context.conditionType.type === 'entryCondition' ? 'ENTRY' : 'EXIT' };
		await api({ url, method: 'post', data, successToaster: true });
		await conditionListAPI();
		setComponentState(null);
	};

	const handleConditionDelete = async e => {
		await api({ url: `workflow/v1/condition/delete/${e}`, method: 'delete', successToaster: true });
		await conditionListAPI();
		setComponentState(null);
	};

	return (
		<>
			{['listing', 'delete', 'trigger'].includes(dockerState) && (
				<>
					<TitleSection add handleAdd={addButtonToggler} />
					{listingDisplay}
					{addPopupContent}
					{dockerContainer}
					{deletePopup}
				</>
			)}
			{['add', 'edit', 'view'].includes(dockerState) && (
				<>
					{gobackSavePopup}
					<WorkflowConfiguration handleSaveRule={handleSaveRule} deleteWfc={handleConditionDelete} />
					<BottomSection handleButtonClick={setSavePopupState} disableSave={dockerState === 'view'} />
				</>
			)}
		</>
	);
};

export default Workflow;
